<template>
	<div :id="typeUploadFile">
		<vs-alert
			v-if="activateListUploadFile && (typeAction !== 'ADD' || typeAction !== 'UPDATE')"
			closable
			class="ml-6 pr-6"
			color="success"
			icon-pack="feather"
			close-icon="icon-x"
			:active.sync="activeAlertText"
		>
			{{ $t("Optimization.TheUploadedFilesAreTemporaryForOptimization") }}
		</vs-alert>
		<vx-card no-shadow>
			<div class="vx-row">
				<div v-show="!maximize" class="vx-col" :class="!activateListUploadFile ? 'w-full' : 'md:w-1/3 w-full'">
					<vx-card card-border no-shadow>
						<div v-if="typeUploadFile === 'JOB_UPLOAD'" class="flex justify-end mb-3">
							<vs-button
								type="border"
								color="primary"
								icon-pack="feather"
								icon="icon-download"
								@click.prevent="downloadJobTemplate()"
							>
								{{ $t("Jobs.DownloadTemplate") }}
							</vs-button>
						</div>
						<vs-input
							:id="inputFileId"
							ref="uploadOptimization"
							type="file"
							class="w-full mb-4"
							accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.json,.csv,.xml"
							@change="onFileChange"
						/>
						<vs-alert :active="false" color="danger" icon="new_releases" class="mt-2">
							{{ $t("UploadFiles.InvalidFormatFileText") }}
						</vs-alert>
						<div
							class="custom-file-upload mt-2 relative file-upload--height--full"
							@dragover="dragover"
							@dragleave="dragleave"
							@drop="drop"
						>
							<!-- Enhanced Progress Overlay con barra de progreso moderna -->
							<div v-if="showProgressOverlay" class="enhanced-upload-progress-overlay">
								<div class="modern-progress-container">
									<!-- Header del progreso -->
									<div class="progress-header">
										<div
											class="progress-icon-modern"
											:class="{
												'success-icon': showSuccessState,
												'redirecting-icon': showRedirectingState,
											}"
										>
											<feather-icon
												:icon="
													showRedirectingState
														? 'ArrowRightIcon'
														: showSuccessState
														? 'CheckCircleIcon'
														: 'UploadCloudIcon'
												"
												class="upload-cloud-icon"
												:class="{
													'animate-bounce':
														progressPercent < 100 &&
														!showSuccessState &&
														!showRedirectingState,
													'success-pulse': showSuccessState && !showRedirectingState,
													'redirecting-pulse': showRedirectingState,
												}"
											/>
										</div>
										<div class="progress-status">
											<h4
												class="progress-main-title"
												:class="{'redirecting-title': showRedirectingState}"
											>
												{{
													showRedirectingState
														? $t("UploadFiles.RedirectingToMapping")
														: showSuccessState
														? $t("UploadFiles.UploadSuccessful")
														: $t("UploadFiles.UploadingFile")
												}}
											</h4>
											<p class="progress-file-name" v-if="!showRedirectingState">
												{{ fileName || $t("UploadFiles.ProcessingFile") }}
											</p>

											<!-- Indicador visual de redirección -->
											<div v-if="showRedirectingState" class="redirecting-indicator">
												<div class="redirecting-dots">
													<span class="dot"></span>
													<span class="dot"></span>
													<span class="dot"></span>
												</div>
												<p class="redirecting-subtitle">
													{{ $t("UploadFiles.PleaseWait") }}
												</p>
											</div>
										</div>
									</div>

									<!-- Barra de progreso moderna -->
									<div class="modern-progress-section">
										<div class="progress-stats">
											<span class="progress-label">{{ $t("UploadFiles.Progress") }}</span>
											<span class="progress-percent">{{ progressPercent }}%</span>
										</div>

										<!-- Barra de progreso personalizada -->
										<div class="custom-progress-bar">
											<div class="progress-track">
												<div
													class="progress-fill"
													:style="{width: progressPercent + '%'}"
													:class="{'progress-complete': progressPercent === 100}"
												>
													<div class="progress-shine"></div>
												</div>
											</div>
										</div>

										<!-- Indicador de velocidad/estado -->
										<div class="progress-details">
											<div class="upload-speed" v-if="progressPercent < 100">
												<feather-icon icon="ZapIcon" class="w-3 h-3 mr-1" />
												<span>{{ $t("UploadFiles.Uploading") }}...</span>
											</div>
											<div class="upload-complete" v-else-if="!showSuccessState">
												<feather-icon
													icon="CheckCircleIcon"
													class="w-3 h-3 mr-1 text-success"
												/>
												<span class="text-success">{{ $t("UploadFiles.Complete") }}</span>
											</div>
											<div class="upload-success-message" v-else>
												<feather-icon icon="CheckIcon" class="w-3 h-3 mr-1 text-success" />
												<span class="text-success font-semibold">{{
													$t("UploadFiles.UploadSuccessMessage")
												}}</span>
											</div>
										</div>
									</div>

									<!-- Acciones -->
									<div class="progress-actions-modern" v-if="!showSuccessState">
										<vs-button
											type="border"
											color="danger"
											size="small"
											icon-pack="feather"
											icon="icon-x"
											class="cancel-upload-btn"
											@click.prevent="cancelUpload"
										>
											{{ $t("Cancel") }}
										</vs-button>
									</div>
								</div>
							</div>
							<label class="custom-file-upload__icon" :class="uploadStatus">
								<feather-icon
									v-if="!dataFile && !isDragging"
									class="mr-2"
									:icon="inputFileIcon"
									svgClasses="h-10 w-10"
								/>
								<feather-icon v-else class="mr-2" icon="FileTextIcon" svgClasses="h-10 w-10" />
							</label>
							<label class="font-semibold text-center mb-2 text-sm" :class="uploadStatus">
								{{
									this.dataFile
										? this.fileName
										: isDragging
										? $t("DropFile")
										: $t("UploadFiles.UploadFileDragAndDrop")
								}}<br />
								<span v-if="!this.dataFile && !isDragging">{{ $t("Or") }}</span
								><br />
							</label>
							<label
								v-if="!this.dataFile && !isDragging"
								class="custom-file-upload__text font-bold text-center"
								:class="uploadStatus"
							>
								<label
									:for="inputFileId"
									class="p-2 text-xs md:text-sm font-semibold border rounded cursor-pointer bg-primary--transparent hover:bg-primary hover:shadow-sm"
								>
									<span class="text-white">{{ $t("UploadFiles.SelectFile") }}</span> </label
								><br /><br />
								<small class="text-xs" :style="'color: #8d8181'">{{
									$t("AllowedFileExtensions") + ": *.xlsx, *.xls, *.csv, *.txt, *.json, *.xml"
								}}</small>
							</label>
							<div v-if="this.dataFile && !showProgressOverlay" class="file-ready-section">
								<div class="file-status-info">
									<feather-icon icon="CheckCircleIcon" class="w-5 h-5 text-success mr-2" />
									<span class="text-success font-medium">{{
										$t("UploadFiles.FileReadyToBeUploaded")
									}}</span>
								</div>
								<div class="file-actions mt-3">
									<vs-button
										color="danger"
										icon-pack="feather"
										icon="icon-x"
										type="border"
										size="small"
										:title="$t('RemoveSelection')"
										@click.prevent="cleanData"
									>
										{{ $t("RemoveSelection") }}
									</vs-button>
								</div>
							</div>
						</div>
						<vs-alert
							v-if="status !== null && this.dataFile"
							class="mt-4 mb-base"
							icon-pack="feather"
							close-icon="icon-x"
							:color="status ? 'success' : 'danger'"
							:title="status ? $t('UploadFiles.UploadSuccess') : $t('UploadFiles.UploadError')"
						>
							{{
								status ? $t("UploadFiles.UploadSuccessMessage") : $t("UploadFiles.UploadErrorMessage")
							}}.
						</vs-alert>
					</vx-card>
				</div>
				<div
					v-if="activateListUploadFile && typeUploadFile === 'VISIT_POINT_UPLOAD'"
					class="vx-col"
					:class="maximize ? 'w-full' : 'md:w-2/3'"
				>
					<vx-card card-border no-shadow>
						<upload-visit-point-list
							:isHeightMedium="true"
							:maximize="maximize"
							@handleMaximizeUploadFile="handleMaximizeUploadFile"
						/>
					</vx-card>
				</div>
				<div
					v-else-if="activateListUploadFile && typeUploadFile === 'VEHICLE_UPLOAD'"
					class="vx-col"
					:class="maximize ? 'w-full' : 'md:w-2/3'"
				>
					<vx-card card-border no-shadow>
						<upload-vehicle-list
							:isHeightMedium="true"
							:maximize="maximize"
							@handleMaximizeUploadFile="handleMaximizeUploadFile"
						/>
					</vx-card>
				</div>
			</div>
			<div v-if="!activateListUploadFile && typeUploadFile !== 'JOB_UPLOAD'" class="vx-row mt-4">
				<div class="vx-col w-full">
					<div class="flex flex-wrap items-center justify-end">
						<vs-button
							icon-pack="feather"
							icon="icon-upload"
							class="ml-2"
							:disabled="enableUploadBtn"
							@click.prevent="handleUpload"
							>{{ $t("Upload") }}
						</vs-button>
					</div>
				</div>
			</div>
			<div
				v-else-if="(typeAction === 'ADD' || typeAction === 'UPDATE') && typeUploadFile !== 'JOB_UPLOAD'"
				class="vx-row mt-4"
			>
				<div class="vx-col w-full">
					<div class="flex flex-wrap items-center justify-end">
						<vs-button
							icon-pack="feather"
							icon="icon-plus"
							class="ml-2"
							:disabled="isDisabledButton"
							@click.prevent="
								typeUploadFile === 'VISIT_POINT_UPLOAD'
									? addUploadFileVisitPoint()
									: addUploadFileVehicle()
							"
							>{{ $t("Add") }}
						</vs-button>
					</div>
				</div>
			</div>
		</vx-card>
		<vs-prompt
			class="prompt-content"
			:title="$t('Frequency.DiscardedItems.Title')"
			:active.sync="activeDiscardedItemsPrompt"
			:cancelText="$t('Routing.Title.Close')"
			:buttons-hidden="true"
			@cancel="closeDiscardedItemsPrompt"
			@close="closeDiscardedItemsPrompt"
		>
			<div class="text-left">
				<p>{{ $t("Frequency.DiscardedItems.Message") }}</p>
				<ul class="mt-2 mb-10 pl-4">
					<li v-for="(item, index) in paginatedDiscardedItems" :key="index" class="mb-2">❌ {{ item }}</li>
				</ul>
				<!-- Pagination -->
				<div v-if="showDiscardedItemsPagination" class="mt-2 mb-10 text-center">
					<vs-pagination
						:total="totalDiscardedItemsPages"
						:max="5"
						v-model="currentDiscardedItemsPage"
						@change="changeDiscardedItemsPage"
					/>
				</div>
			</div>
		</vs-prompt>
	</div>
</template>
<script>
import {uploadFileFormOptimization} from "@/components/optimization/upload-file/model/UploadFileFormOptimizationMixin.js";

export default {
	mixins: [uploadFileFormOptimization],
};
</script>
<style lang="scss">
input[type="file"] {
	display: none;
}

.custom-file-upload {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border: 2px dashed #ccc;
	border-radius: 5px;
}

.custom-file-upload__icon {
	padding: 50px 100% 0px 100%;
}

.custom-file-upload__text {
	padding: 0px 50px 50px 50px;
}

.custom-heigh {
	height: 70vh;
}

.btn--span-text {
	display: block;
}

.bg-success--transparent {
	background-color: rgba(var(--vs-success), 0.2) !important;
}

.bg-primary--transparent {
	background-color: rgba(var(--vs-primary), 0.8) !important;
}

@media only screen and (min-width: 481px) {
	.btn--span-text {
		display: block;
	}
}

@media only screen and (min-width: 768px) {
	.btn--span-text {
		display: none;
	}
}

@media only screen and (min-width: 1289px) {
	.btn--span-text {
		display: block;
	}
}
</style>
<style lang="scss" scoped>
.file-upload--height--full {
	height: 570px;
}

/* Enhanced Modern Progress Overlay */
.enhanced-upload-progress-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
	backdrop-filter: blur(12px);
	border-radius: 8px;
	border: 2px solid rgba(var(--vs-primary), 0.1);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	animation: modernOverlayFadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

@keyframes modernOverlayFadeIn {
	from {
		opacity: 0;
		transform: scale(0.9) translateY(10px);
	}
	to {
		opacity: 1;
		transform: scale(1) translateY(0);
	}
}

.modern-progress-container {
	width: 100%;
	max-width: 280px;
	padding: 1.5rem;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

/* Header Section */
.progress-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.progress-icon-modern {
	flex-shrink: 0;
	width: 2.5rem;
	height: 2.5rem;
	background: linear-gradient(135deg, rgba(var(--vs-primary), 0.1) 0%, rgba(var(--vs-primary), 0.05) 100%);
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.upload-cloud-icon {
	width: 1.5rem;
	height: 1.5rem;
	color: rgba(var(--vs-primary), 1);
	transition: all 0.3s ease;
}

.progress-icon-modern.success-icon {
	background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%);
}

.progress-icon-modern.success-icon .upload-cloud-icon {
	color: #22c55e;
}

.success-pulse {
	animation: successPulse 1s ease-in-out;
}

.redirecting-pulse {
	animation: redirectingMove 1.5s ease-in-out infinite;
	color: #059669 !important;
}

.redirecting-icon {
	background: linear-gradient(135deg, #059669, #10b981) !important;
	border: 2px solid #059669;
	animation: iconRedirectPulse 1s ease-in-out infinite;
}

@keyframes successPulse {
	0% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
	100% {
		transform: scale(1);
	}
}

.progress-status {
	flex: 1;
	min-width: 0;
}

.progress-main-title {
	font-size: 1rem;
	font-weight: 600;
	color: #1e293b;
	margin: 0 0 0.25rem 0;
	line-height: 1.2;
}

.progress-file-name {
	font-size: 0.8rem;
	color: #64748b;
	margin: 0;
	line-height: 1.2;
	word-break: break-all;
}

/* Progress Section */
.modern-progress-section {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.progress-stats {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.progress-label {
	font-size: 0.8rem;
	font-weight: 500;
	color: #475569;
}

.progress-percent {
	font-size: 0.9rem;
	font-weight: 700;
	color: rgba(var(--vs-primary), 1);
}

/* Custom Progress Bar */
.custom-progress-bar {
	width: 100%;
}

.progress-track {
	width: 100%;
	height: 8px;
	background: #e2e8f0;
	border-radius: 8px;
	overflow: hidden;
	position: relative;
}

.progress-fill {
	height: 100%;
	background: linear-gradient(
		90deg,
		rgba(var(--vs-primary), 0.8) 0%,
		rgba(var(--vs-primary), 1) 50%,
		rgba(var(--vs-primary), 0.9) 100%
	);
	border-radius: 8px;
	position: relative;
	transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	overflow: hidden;
}

.progress-fill.progress-complete {
	background: linear-gradient(90deg, #10b981 0%, #059669 50%, #10b981 100%);
}

.progress-shine {
	position: absolute;
	top: 0;
	left: -100%;
	width: 100%;
	height: 100%;
	background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%);
	animation: progressShine 2s infinite;
}

@keyframes progressShine {
	0% {
		left: -100%;
	}
	100% {
		left: 100%;
	}
}

/* Progress Details */
.progress-details {
	display: flex;
	align-items: center;
	justify-content: center;
}

.upload-speed,
.upload-complete {
	display: flex;
	align-items: center;
	font-size: 0.75rem;
	font-weight: 500;
	color: #64748b;
}

.upload-complete {
	color: #10b981 !important;
}

.upload-success-message {
	display: flex;
	align-items: center;
	font-size: 0.75rem;
	font-weight: 600;
	color: #10b981 !important;
	animation: fadeInSuccess 0.5s ease-in-out;
}

/* Redirecting Indicator Styles */
.redirecting-indicator {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	margin-top: 0.5rem;
	animation: fadeInRedirect 0.6s ease-in-out;
}

.redirecting-dots {
	display: flex;
	gap: 0.3rem;
	align-items: center;
}

.redirecting-dots .dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: #059669;
	animation: dotPulse 1.4s ease-in-out infinite;
}

.redirecting-dots .dot:nth-child(1) {
	animation-delay: 0s;
}

.redirecting-dots .dot:nth-child(2) {
	animation-delay: 0.2s;
}

.redirecting-dots .dot:nth-child(3) {
	animation-delay: 0.4s;
}

.redirecting-subtitle {
	font-size: 0.7rem;
	color: #059669;
	font-weight: 500;
	text-align: center;
	margin: 0;
	opacity: 0.8;
}

@keyframes fadeInSuccess {
	from {
		opacity: 0;
		transform: translateY(5px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInRedirect {
	from {
		opacity: 0;
		transform: translateY(3px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes pulseRedirect {
	0%,
	100% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.05);
		opacity: 0.8;
	}
}

@keyframes dotPulse {
	0%,
	20% {
		transform: scale(1);
		opacity: 0.3;
	}
	50% {
		transform: scale(1.2);
		opacity: 1;
	}
	80%,
	100% {
		transform: scale(1);
		opacity: 0.3;
	}
}

@keyframes redirectingMove {
	0%,
	100% {
		transform: translateX(0);
	}
	50% {
		transform: translateX(5px);
	}
}

@keyframes iconRedirectPulse {
	0%,
	100% {
		transform: scale(1);
		box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.4);
	}
	50% {
		transform: scale(1.1);
		box-shadow: 0 0 0 10px rgba(5, 150, 105, 0);
	}
}

/* Actions */
.progress-actions-modern {
	display: flex;
	justify-content: center;
	margin-top: 0.5rem;
}

.cancel-upload-btn {
	border-radius: 6px !important;
	font-size: 0.8rem !important;
	padding: 0.4rem 1rem !important;
	transition: all 0.2s ease !important;
}

.cancel-upload-btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
}

/* File Ready Section Styles */
.file-ready-section {
	padding: 1rem;
	text-align: center;
	border-radius: 0.5rem;
	background: rgba(34, 197, 94, 0.05);
	border: 1px solid rgba(34, 197, 94, 0.2);
}

.file-status-info {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 0.75rem;
}

.file-actions {
	display: flex;
	justify-content: center;
}

/* Responsive Design */
@media (max-width: 768px) {
	.modern-progress-container {
		max-width: 240px;
		padding: 1rem;
		gap: 1rem;
	}

	.progress-icon-modern {
		width: 2rem;
		height: 2rem;
	}

	.upload-cloud-icon {
		width: 1.25rem;
		height: 1.25rem;
	}

	.progress-main-title {
		font-size: 0.9rem;
	}

	.progress-file-name {
		font-size: 0.7rem;

		&.redirecting-message {
			animation: fadeInRedirect 0.5s ease-in;
			color: #059669; /* text-emerald-600 */
			font-weight: 500;
		}
	}

	.redirecting-title {
		animation: pulseRedirect 1s ease-in-out infinite;
		color: #059669 !important;
		font-weight: 600 !important;
		font-size: 1rem !important;
	}

	.progress-track {
		height: 6px;
	}

	.progress-label,
	.progress-percent {
		font-size: 0.75rem;
	}
}

@media (max-width: 480px) {
	.modern-progress-container {
		max-width: 200px;
		padding: 0.75rem;
	}

	.progress-header {
		gap: 0.5rem;
	}

	.progress-main-title {
		font-size: 0.85rem;
	}

	.progress-file-name {
		font-size: 0.65rem;
	}
}
</style>
